import { useState } from "react";
import { FiSearch, FiMoreVertical } from "react-icons/fi";
import { MessagePopup } from "../messages/MessagePopup";

interface Message {
  id: number;
  sender: string;
  title: string;
  description: string;
  timestamp: string;
  avatar?: string;
  isRead?: boolean;
  role?: string;
  fullMessage?: string;
}

const mockMessages: Message[] = [
  {
    id: 1,
    sender: "Adeyemi Ojo",
    title: "New Compliance Guidelines",
    description: "Please review the updated guideline for product sanitization",
    timestamp: "5m ago",
    role: "System Administrator",
    fullMessage:
      "Hello Kehinde,\n\nI hope this message finds you well. There have been some important updates to our product sanitization guidelines that I need you to review. Please take some time to go through it carefully, as these changes will impact your team. Let me know if you have any questions.",
    isRead: false,
  },
  {
    id: 2,
    sender: "Jasmine Lee",
    title: "Quarterly Sales Report",
    description:
      "Attached is the report detailing sales performance over the quarter",
    timestamp: "5m ago",
    role: "Sales Manager",
    fullMessage:
      "Hello,\n\nPlease find attached the quarterly sales report with detailed metrics.",
    isRead: false,
  },
  {
    id: 3,
    sender: "Adeyemi Ojo",
    title: "New Compliance Guidelines",
    description: "Please review the updated guideline for product sanitization",
    timestamp: "5m ago",
    role: "System Administrator",
    fullMessage:
      "Hello Kehinde,\n\nI hope this message finds you well. There have been some important updates...",
    isRead: true,
  },
  {
    id: 4,
    sender: "Michael Chen",
    title: "Product Launch Update",
    description: "Exciting new features will be available in the next release",
    timestamp: "5m ago",
    role: "Product Manager",
    fullMessage:
      "Hello,\n\nI'm excited to share that we have some great new features coming in the next release...",
    isRead: true,
  },
  {
    id: 5,
    sender: "Sarah Johnson",
    title: "Q4 Sales Report",
    description:
      "The sales report for Q4 is now available for review. Key insights included",
    timestamp: "5m ago",
    role: "Sales Director",
    fullMessage:
      "Hello,\n\nPlease review the attached Q4 sales report with key insights...",
    isRead: true,
  },
];

export function MessageInbox() {
  const [messages, setMessages] = useState<Message[]>(mockMessages);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "unread" | "read">(
    "all",
  );

  const filteredMessages = messages.filter((msg) => {
    const matchesSearch =
      msg.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === "all" ||
      (filterType === "unread" && !msg.isRead) ||
      (filterType === "read" && msg.isRead);

    return matchesSearch && matchesFilter;
  });

  const unreadCount = messages.filter((msg) => !msg.isRead).length;

  const handleSelectMessage = (message: Message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
    // Mark as read
    setMessages(
      messages.map((msg) =>
        msg.id === message.id ? { ...msg, isRead: true } : msg,
      ),
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const handleMarkAllAsRead = () => {
    setMessages(messages.map((msg) => ({ ...msg, isRead: true })));
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      "from-indigo-400 to-indigo-600",
      "from-blue-400 to-blue-600",
      "from-purple-400 to-purple-600",
      "from-green-400 to-green-600",
      "from-orange-400 to-orange-600",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <>
      <div className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
              <p className="text-sm text-gray-500 mt-1">
                {unreadCount} unread messages
              </p>
            </div>
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition"
            >
              Mark all as read
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              {(["all", "unread", "read"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    filterType === type
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No messages found</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => handleSelectMessage(message)}
                className={`p-4 cursor-pointer transition hover:bg-gray-50 ${
                  !message.isRead ? "bg-indigo-50" : ""
                }`}
              >
                <div className="flex gap-4">
                  <div className="relative flex-shrink-0">
                    <div
                      className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(
                        message.sender,
                      )} flex items-center justify-center text-white font-bold`}
                    >
                      {message.sender.charAt(0)}
                    </div>
                    {!message.isRead && (
                      <div className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1">
                        <p
                          className={`text-sm ${message.isRead ? "font-normal text-gray-600" : "font-semibold text-gray-900"}`}
                        >
                          {message.sender}
                        </p>
                        <p
                          className={`text-sm line-clamp-1 ${
                            message.isRead
                              ? "text-gray-500"
                              : "font-medium text-gray-700"
                          }`}
                        >
                          {message.title}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {message.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {message.description}
                    </p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  >
                    <FiMoreVertical size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selectedMessage && (
        <MessagePopup
          isOpen={isModalOpen}
          message={selectedMessage}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
